/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { supabase, setAuthToken, getAuthToken, getSupabase } from '@/lib/supabase';
import { SupabaseClient } from '@supabase/supabase-js';

/**
 * SupabaseProvider - Provides authenticated Supabase client via React Context
 *
 * This provider:
 * 1. Gets Clerk JWT token (signed with Supabase secret)
 * 2. Sets the global auth token for all Supabase requests
 * 3. Enables RLS policies to use auth.uid() / auth.jwt()
 * 4. Refreshes token when tab regains focus (prevents stale token after background)
 *
 * SETUP REQUIRED in Clerk Dashboard:
 * 1. Go to JWT Templates → Create new template
 * 2. Name: "supabase"
 * 3. Claims:
 *    {
 *      "aud": "authenticated",
 *      "role": "authenticated",
 *      "sub": "{{user.id}}",
 *      "email": "{{user.primary_email_address}}"
 *    }
 * 4. Signing algorithm: HS256
 * 5. Signing key: Your Supabase JWT Secret
 *    (from Supabase Dashboard → Settings → API → JWT Secret)
 */

interface SupabaseContextValue {
  client: SupabaseClient;
  isAuthenticated: boolean;
  userId: string | null;
  isLoading: boolean;
  hasJwtTemplate: boolean;
  /** Increments each time token is refreshed after tab regains focus */
  tokenVersion: number;
}

const SupabaseContext = createContext<SupabaseContextValue>({
  client: supabase,
  isAuthenticated: false,
  userId: null,
  isLoading: true,
  hasJwtTemplate: false,
  tokenVersion: 0,
});

// Template name in Clerk Dashboard
const CLERK_SUPABASE_TEMPLATE = 'supabase';

// Token refresh interval (50 seconds - aggressive to prevent JWT expiry)
// Clerk caches tokens and returns fresh ones, so this is cheap
const TOKEN_REFRESH_INTERVAL = 50 * 1000;

interface SupabaseProviderProps {
  children: React.ReactNode;
}

export function SupabaseProvider({ children }: SupabaseProviderProps) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [hasJwtTemplate, setHasJwtTemplate] = useState(false);
  const [tokenVersion, setTokenVersion] = useState(0);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Lock to prevent concurrent token refreshes (race condition fix)
  const isRefreshingRef = useRef(false);
  // Track whether initial auth has completed to prevent re-initialization loops
  const initializedRef = useRef(false);
  // Store hasJwtTemplate in ref to avoid stale closure in refreshToken
  const hasJwtTemplateRef = useRef(false);

  const refreshToken = useCallback(async () => {
    // Prevent concurrent refreshes - if one is in progress, skip
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;

    try {
      if (!isSignedIn) {
        setAuthToken(null);
        setHasJwtTemplate(false);
        hasJwtTemplateRef.current = false;
        return;
      }

      // Get fresh JWT token from Clerk - skipCache ensures we always get a new one
      const token = await getToken({ template: CLERK_SUPABASE_TEMPLATE, skipCache: true });

      if (token) {
        setAuthToken(token);
        setHasJwtTemplate(true);
        hasJwtTemplateRef.current = true;
      } else {
        // Template not configured - this is expected if user hasn't set it up
        // Don't clear existing token during refresh to avoid race conditions
        if (!hasJwtTemplateRef.current) {
          setAuthToken(null);
        }
        setHasJwtTemplate(false);
        hasJwtTemplateRef.current = false;
      }
    } catch {
      // Template doesn't exist - fall back to anon
      // Don't clear existing token if we already had one (transient error)
      if (!hasJwtTemplateRef.current) {
        setAuthToken(null);
      }
      setHasJwtTemplate(false);
      hasJwtTemplateRef.current = false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [isSignedIn, getToken]); // Removed hasJwtTemplate - use ref instead to prevent recreating callback

  // Initial auth setup - runs only once when Clerk is loaded
  useEffect(() => {
    if (!isLoaded || initializedRef.current) return;

    const initialize = async () => {
      setIsLoading(true);
      try {
        await refreshToken();
        // If signed in but no token, retry once after a small delay
        // This handles the case where Clerk needs a moment to provide the token
        if (isSignedIn && !getAuthToken()) {
          await new Promise(resolve => setTimeout(resolve, 200));
          await refreshToken();
        }
      } finally {
        initializedRef.current = true;
        setIsLoading(false);
      }
    };

    initialize();
  }, [isLoaded, refreshToken, isSignedIn]);

  // Set up token refresh interval
  useEffect(() => {
    if (!isSignedIn || !hasJwtTemplate) {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    // Refresh token periodically
    refreshIntervalRef.current = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [isSignedIn, hasJwtTemplate, refreshToken]);

  // Refresh token immediately when tab regains focus
  // Browsers throttle setInterval in background tabs, so the token may be stale
  // when the user returns. This ensures we get a fresh token right away.
  useEffect(() => {
    if (!isSignedIn || !hasJwtTemplateRef.current) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        await refreshToken();
        // Bump version so consumers (React Query) know to refetch
        setTokenVersion(v => v + 1);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSignedIn, refreshToken]);

  // Clear token on sign out and reset initialized flag for re-login
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      setAuthToken(null);
      setHasJwtTemplate(false);
      hasJwtTemplateRef.current = false;
      initializedRef.current = false;
    }
  }, [isLoaded, isSignedIn]);

  const value: SupabaseContextValue = {
    client: getSupabase(),
    isAuthenticated: isSignedIn ?? false,
    userId: user?.id ?? null,
    isLoading: !isLoaded || isLoading,
    hasJwtTemplate,
    tokenVersion,
  };

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
}

/**
 * Hook to get the Supabase client
 * Returns authenticated client if JWT is set, otherwise anon client
 */
export function useSupabaseClient(): SupabaseClient {
  return getSupabase();
}

/**
 * Hook to get full Supabase context (client + auth info)
 */
export function useSupabase(): SupabaseContextValue {
  return useContext(SupabaseContext);
}

export default SupabaseProvider;
